"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import {
  BookOpen,
  FileText,
  MessageSquare,
  School,
  AlertCircle,
  PenTool,
  X,
  Trash2,
  Bell,
  RefreshCw,
} from "lucide-react"
import { getAllNotification, getClassData } from "./api"
import { postAws } from "../postAws"
import { useDispatch } from "react-redux"
import { setClass, setSubject, setClassCode, setClassData } from "../features/teachersubject/teacherSubSlice"

const iconMap = {
  assignment: FileText,
  quiz: PenTool,
  library: BookOpen,
  announcement: MessageSquare,
  classroom: School,
  general: AlertCircle,
}

const iconColors = {
  assignment: "text-blue-600",
  quiz: "text-green-600",
  library: "text-amber-600",
  announcement: "text-purple-600",
  classroom: "text-red-600",
  general: "text-gray-600",
}

const safeStringify = (obj) => {
  const cache = new Set()
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (cache.has(value)) {
        return "[Circular]"
      }
      cache.add(value)
    }
    return value
  })
}

const storeClassData = (classResponse, dispatch) => {
  if (classResponse.statusCode === 200 && classResponse.body && classResponse.body.data) {
    const classDataString = safeStringify(classResponse.body.data)
    localStorage.setItem("classData", classDataString)
    dispatch(setClassData(classResponse.body.data))
  }
}

const NotificationBell = () => {
  const [isMobile, setIsMobile] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [findNotifications, setFindNotifications] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const bellRef = useRef(null)

  // Check if mobile view
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Initial check
    checkIfMobile()

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile)

    // Cleanup
    return () => window.removeEventListener("resize", checkIfMobile)
  }, [])

  const unseenCount = notifications.filter((n) => n.seen === "N").length

  const handleClick = async (notification) => {
    localStorage.setItem("class", notification.classValue)
    localStorage.setItem("subject", notification.subject)
    localStorage.setItem("classCode", notification.classCode)
    localStorage.setItem("classRoomId", notification.classRoomId)

    dispatch(setClass(notification.classValue))
    dispatch(setSubject(notification.subject))
    dispatch(setClassCode(notification.classCode))

    try {
      const classResponse = await getClassData(notification.classRoomId)
      storeClassData(classResponse, dispatch)
    } catch (error) {
      console.error("Error fetching class data:", error)
    }

    switch (notification.refType) {
      case "assignment":
        navigate("/assignments")
        break
      case "quiz":
        navigate("/quiz")
        break
      case "library":
        navigate("/library")
        break
      case "announcement":
        navigate("/Announcements")
        break
      case "classroom":
        navigate("/students")
        break
      default:
        navigate("/dashboard")
        break
    }

    window.location.reload()
  }

  const fetchNotifications = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const fetchedNotifications = await getAllNotification(localStorage.getItem("teacherId"))
      if (fetchedNotifications && fetchedNotifications.body) {
        setNotifications(fetchedNotifications.body)
      } else {
        throw new Error("Invalid response format")
      }
    } catch (err) {
      setError("Failed to fetch notifications")
      console.error("Error fetching notifications:", err)
    } finally {
      setIsLoading(false)
    }
  }

  const refreshNotifications = async () => {
    setIsRefreshing(true)
    try {
      await fetchNotifications()
    } finally {
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    if (!findNotifications) {
      fetchNotifications()
      setFindNotifications(true)
    }
  }, [findNotifications])

  useEffect(() => {
    const interval = setInterval(() => {
      setFindNotifications(false)
    }, 15000)

    return () => clearInterval(interval)
  }, [])

  const toggleNotifications = async () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setNotifications(notifications.map((n) => ({ ...n, seen: "Y" })))
      try {
        await postAws("https://api-dev.aurait.ai/alert/seen", "POST", {
          id: localStorage.getItem("teacherId"),
        })
      } catch (error) {
        console.error("Error marking notifications as seen:", error)
      }
    }
  }

  const clearAllNotification = async () => {
    try {
      await postAws("https://api-dev.aurait.ai/alert/clearAll", "POST", {
        userId: localStorage.getItem("teacherId"),
      })
      setNotifications([])
    } catch (error) {
      console.error("Error clearing notifications:", error)
    }
  }

  const closeNotifications = () => {
    setIsOpen(false)
  }

  const deleteNotification = async (id, event) => {
    event.stopPropagation()
    try {
      await postAws("https://api-dev.aurait.ai/alert/delete", "POST", { id })
      setNotifications(notifications.filter((n) => n._id !== id))
    } catch (error) {
      console.error("Error deleting notification:", error)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const formatNotificationTime = (timestamp) => {
    const now = new Date()
    const notificationTime = new Date(timestamp)
    const diffInSeconds = Math.floor((now - notificationTime) / 1000)

    if (diffInSeconds < 60) {
      return "Just now"
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes}m ago`
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return `${hours}h ago`
    } else {
      return notificationTime.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    }
  }

  return (
    <div className={`relative inline-block ${isMobile ? "-mt-2" : "mt-2"}`} ref={bellRef}>
      <button
        onClick={toggleNotifications}
        className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
        style={{ WebkitTapHighlightColor: "transparent" }}
        aria-label="Toggle notifications"
      >
        <Bell className={`w-6 h-6 ${unseenCount > 0 ? "text-indigo-500" : "text-gray-600"}`} />
        {unseenCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75 animate-ping"></span>
            <span className="relative inline-flex rounded-full h-5 w-5 bg-indigo-500 text-white text-xs font-medium items-center justify-center">
              {unseenCount > 9 ? "9+" : unseenCount}
            </span>
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 md:absolute md:inset-auto md:right-0 md:top-full md:mt-1 md:w-96">
          {/* Mobile overlay background */}
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm md:hidden" onClick={closeNotifications} />

          {/* Notification panel */}
          <div className="fixed inset-x-0 bottom-0 md:bottom-auto w-full md:w-80 bg-white rounded-t-2xl md:rounded-xl shadow-2xl md:absolute md:right-0 animate-slide-up md:animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">Notifications</h3>
                {unseenCount > 0 && (
                  <span className="bg-indigo-100 text-indigo-600 text-xs font-medium px-2 py-0.5 rounded-full">
                    {unseenCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={refreshNotifications}
                  disabled={isRefreshing}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full"
                >
                  <RefreshCw className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`} />
                </button>
                <button onClick={closeNotifications} className="p-2 text-gray-400 hover:text-gray-600 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Notification list */}
            <div className="overflow-y-auto max-h-[80vh] md:max-h-[60vh]">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                    <Bell className="w-8 h-8 text-indigo-400 animate-pulse" />
                  </div>
                  <p className="text-sm text-gray-500">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                    <Bell className="w-8 h-8 text-indigo-400" />
                  </div>
                  <p className="text-base font-medium text-gray-900 mb-1">No notifications</p>
                  <p className="text-sm text-gray-500 text-center">
                    You're all caught up! New notifications will appear here.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => {
                    const IconComponent = iconMap[notification.refType] || AlertCircle
                    const iconColorClass = iconColors[notification.refType] || "text-gray-600"
                    const isUnread = notification.seen === "N"

                    return (
                      <div
                        key={notification._id}
                        onClick={() => handleClick(notification)}
                        className={`px-4 py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors ${
                          isUnread ? "bg-indigo-50/50" : ""
                        }`}
                      >
                        <div className="flex gap-3">
                          <IconComponent className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColorClass}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${isUnread ? "font-medium text-gray-900" : "text-gray-800"}`}>
                              {notification.alertMessage}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                {notification.classValue}-{notification.subject}
                              </span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">
                                {formatNotificationTime(notification.createdAt)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => deleteNotification(notification._id, e)}
                            className="p-2 text-gray-400 hover:text-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Mobile close button */}
            <div className="md:hidden p-4 border-t">
              <button
                onClick={closeNotifications}
                className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell

